<template>
  <div class="design-preview" :class="{ 'design-preview--cover': fit === 'cover' }">
    <div v-if="page" class="design-preview__page" :class="{ 'design-preview__page--flat': flat }" :style="pageStyle">
      <div class="design-preview__bg" :style="bgStyle"></div>
      <div
        v-for="(layer, index) in page.layers || []"
        :key="layer.id || index"
        class="design-preview__layer"
        :style="elStyle(layer)"
      >
        <div v-if="layer.type === 'text'" class="design-preview__text" :style="{ whiteSpace: layer.nowrap ? 'pre' : 'pre-wrap' }"><span>{{ layer.text }}</span></div>
        <video
          v-else-if="layer.type === 'video'"
          :src="layer.src"
          muted
          playsinline
          :autoplay="!flat"
          :loop="!flat"
          preload="metadata"
          :style="mediaStyle(layer)"
        />
        <svg
          v-else-if="layer.type === 'image' && layer.path"
          class="design-preview__shape"
          :viewBox="`0 0 ${layer.viewBoxW || 64} ${layer.viewBoxH || 64}`"
          preserveAspectRatio="none"
        >
          <defs>
            <clipPath :id="'pvclip-' + (layer.id || index)">
              <path :d="layer.path" />
            </clipPath>
          </defs>
          <image
            :href="layer.src"
            :x="layer.imageW ? layer.imageX : 0"
            :y="layer.imageW ? layer.imageY : 0"
            :width="layer.imageW || layer.viewBoxW || 64"
            :height="layer.imageH || layer.viewBoxH || 64"
            :preserveAspectRatio="layer.objectFit === 'cover' || !layer.imageW ? 'xMidYMid slice' : 'none'"
            :clip-path="'url(#pvclip-' + (layer.id || index) + ')'"
          />
        </svg>
        <img
          v-else-if="layer.type === 'image'"
          :src="layer.src"
          alt=""
          draggable="false"
          :style="mediaStyle(layer)"
        />
        <div v-else-if="layer.type === 'icon'" class="design-preview__icon">
          <i :class="['fas', `fa-${layer.icon}`]"></i>
        </div>
        <div v-else class="design-preview__shape" :style="shapeStyle(layer)"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { parseDocument, layerCssFilter } from './presets'

export default {
  props: {
    template: { type: Object, default: null },
    pageData: { type: Object, default: null },
    maxSize: { type: Number, default: 180 },
    flat: { type: Boolean, default: false },
    /** contain: fit inside maxSize; cover: fill parent (crop) */
    fit: { type: String, default: 'contain' },
  },
  computed: {
    page() {
      if (this.pageData) return this.pageData
      if (!this.template) return null
      const doc = parseDocument(this.template.layers, this.template)
      const page = doc.pages && doc.pages[0]
      if (!page) return null
      if (
        this.template.background_url
        && !page.backgroundUrl
        && !(page.layers || []).some((l) => l.type === 'image' || l.type === 'video')
      ) {
        return { ...page, backgroundUrl: this.template.background_url }
      }
      return page
    },
    pageWidth() {
      return this.page.width || (this.template && this.template.width) || 1080
    },
    pageHeight() {
      return this.page.height || (this.template && this.template.height) || 1080
    },
    scale() {
      if (!this.page) return 0.1
      const w = this.pageWidth
      const h = this.pageHeight
      if (this.fit === 'cover') {
        const box = 400
        return Math.max(box / w, box / h)
      }
      return Math.min(this.maxSize / w, this.maxSize / h)
    },
    pageStyle() {
      const w = this.pageWidth
      const h = this.pageHeight
      const style = {
        width: `${w * this.scale}px`,
        height: `${h * this.scale}px`,
      }
      if (this.fit === 'cover') {
        style.position = 'absolute'
        style.left = '50%'
        style.top = '50%'
        style.transform = 'translate(-50%, -50%)'
      }
      return style
    },
    bgStyle() {
      return {
        backgroundColor: this.page.backgroundColor || '#ffffff',
        backgroundImage: this.page.backgroundUrl ? `url(${this.page.backgroundUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    },
  },
  methods: {
    imageFilter(layer) {
      return layerCssFilter(layer)
    },
    mediaStyle(layer) {
      const style = { filter: this.imageFilter(layer) }
      if (layer.objectFit === 'cover') style.objectFit = 'cover'
      if (layer.scale > 1.02) style.transform = `scale(${layer.scale})`
      return style
    },
    elStyle(layer) {
      const s = this.scale
      const style = {
        left: `${layer.x * s}px`,
        top: `${layer.y * s}px`,
        width: `${layer.width * s}px`,
        height: `${(layer.height || layer.fontSize || 40) * s}px`,
        opacity: layer.opacity == null ? 1 : layer.opacity,
        transform: `rotate(${layer.rotation || 0}deg)`,
        transformOrigin: (layer.rotOx || layer.rotOy) ? `${layer.rotOx * s}px ${layer.rotOy * s}px` : 'center center',
        zIndex: (this.page.layers || []).indexOf(layer) + 1,
        overflow: (layer.type === 'image' || layer.type === 'video') && (!layer.shadow || layer.objectFit === 'cover') ? 'hidden' : undefined,
        filter: layer.shadow && layer.type !== 'text' ? 'drop-shadow(6px 10px 12px rgba(0,0,0,0.45))' : undefined,
      }
      if (layer.type === 'icon') {
        style.color = layer.color || '#0d0c22'
        style.fontSize = `${Math.min(layer.width, layer.height || layer.width) * s * 0.82}px`
        style.display = 'flex'
        style.alignItems = 'center'
        style.justifyContent = 'center'
      } else if (layer.type === 'text') {
        const align = layer.align || 'center'
        const vAlign = layer.verticalAlign || 'center'
        style.display = 'flex'
        style.flexDirection = 'column'
        style.justifyContent = vAlign === 'top' || vAlign === 'start' ? 'flex-start' : (vAlign === 'bottom' || vAlign === 'end' ? 'flex-end' : 'center')
        style.alignItems = 'stretch'
        style.overflow = 'visible'
        style.fontFamily = `'${layer.fontFamily || 'Montserrat'}', sans-serif`
        style.fontSize = `${(layer.fontSize || 48) * s}px`
        style.fontWeight = layer.fontWeight || 700
        style.fontStyle = layer.fontStyle || 'normal'
        style.fontSynthesis = 'none'
        style.color = layer.color || '#0d0c22'
        style.textAlign = align
        style.letterSpacing = `${(layer.letterSpacing || 0) * s}px`
        style.lineHeight = layer.lineHeight || 1.12
        style.whiteSpace = layer.nowrap ? 'pre' : 'pre-wrap'
        style.textTransform = layer.uppercase ? 'uppercase' : 'none'
        style.textDecoration = layer.underline ? 'underline' : 'none'
        style.textShadow = layer.shadow ? `0 ${4 * s}px ${12 * s}px rgba(0,0,0,0.35)` : 'none'
        style.webkitTextStroke = layer.outline ? `${(layer.outlineWidth || 4) * s * 0.35}px ${layer.outlineColor || '#000'}` : '0'
      }
      return style
    },
    shapeStyle(layer) {
      const s = this.scale
      const style = {
        background: layer.fill,
        borderRadius: layer.shape === 'ellipse' ? '50%' : `${(layer.radius || 0) * s}px`,
        border: layer.strokeWidth ? `${layer.strokeWidth * s}px solid ${layer.stroke}` : 'none',
      }
      if (layer.shape === 'triangle') style.clipPath = 'polygon(50% 0%, 100% 100%, 0% 100%)'
      if (layer.shape === 'star') style.clipPath = 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
      if (layer.shape === 'line') {
        style.border = 'none'
        style.background = layer.fill
        style.borderRadius = '99px'
      }
      return style
    },
  },
}
</script>

<style scoped>
.design-preview {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.design-preview--cover {
  position: relative;
  overflow: hidden;
}
.design-preview__page {
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  border-radius: 3px;
  box-shadow: 0 8px 20px rgba(13, 12, 34, 0.12);
}
.design-preview__page--flat {
  border-radius: 0;
  box-shadow: none;
}
.design-preview__bg {
  position: absolute;
  inset: 0;
  background-repeat: no-repeat;
  z-index: 0;
}
.design-preview__bg-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  pointer-events: none;
  z-index: 0;
}
.design-preview__layer {
  position: absolute;
  overflow: visible;
  pointer-events: none;
}
.design-preview__text {
  width: 100%;
  height: auto;
  white-space: inherit;
  word-break: normal;
  overflow-wrap: normal;
  overflow: visible;
  line-height: inherit;
  text-align: inherit;
}
.design-preview__icon {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  font-size: inherit;
  line-height: 1;
}
.design-preview__layer img,
.design-preview__layer video,
.design-preview__shape {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: fill;
  pointer-events: none;
}
</style>
